import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as Scrivito from "scrivito";
import { isEqual } from "lodash-es";
import { ProductListQuery } from "api/queries";
import { fetchData } from "utils";
import type { FetchError } from "utils";
import { getHomepage } from "utils";
import type {
  NavigateToConfiguratorProps,
  NavigateToProductListProps,
  GenerateAllProductsProps,
  GetProductStateProps,
  ProductNode,
} from "interfaces/product/product.types";
import type { CombinedError } from "urql";
import { getProductActions } from "stores/product";

type ProductsOverwiewBase = {
  productListData: ProductNode[];
  allProducts: ProductNode[];
  isLoading: boolean;
  waitingFor3dModel: boolean;
  error: FetchError | null;
  productCategoryId: string[];
  productsContent: ProductNode[];
  categoryHeadline: string;
  isImageFetching: boolean;
  is3dModelError: CombinedError | undefined | null;

  productListPage: Scrivito.Obj | null | undefined;
  productConfiguratorPage: Scrivito.Obj | null | undefined;
  createProductAction: null;
  cacheKey: string;
  sidebarChangeRef: {
    current: boolean;
  };
};

type ProductsOverviewActions = {
  getProductsData: (props: GetProductStateProps) => Promise<void>;
  addKeyLists: (
    parentKeyList: string[],
    prods?: ProductNode[],
  ) => ProductNode[];
  generateAllProducts: (props: GenerateAllProductsProps) => void;
  setProductCategoryId: (newVal: string[]) => void;
  markSidebarChange: () => void;
  resetSidebarChange: () => void;
  setProductsContent: (content: ProductNode[]) => void;
  setCategoryHeadline: (headline: string) => void;
  setWaitingFor3dModel: (value: boolean) => void;
  setIs3dModelError: (error: CombinedError | undefined) => void;
  resetProductConfiguratorStore: () => void;
  navigateToProductListPage: (
    params: NavigateToProductListProps | object,
  ) => void;
  navigateToConfiguratorPage: (
    params: NavigateToConfiguratorProps | object,
  ) => void;
};

type ProductsOverwiewState = ProductsOverwiewBase & {
  actions: ProductsOverviewActions;
};

const initialState = {
  productListData: [],
  allProducts: [],
  isLoading: false,
  waitingFor3dModel: false,
  error: null,
  productCategoryId: [],
  productsContent: [],
  categoryHeadline: "",
  isImageFetching: false,
  productListPage: null,
  productConfiguratorPage: null,
  createProductAction: null,
  cacheKey: "",
  sidebarChangeRef: { current: false },
  is3dModelError: null,
};

export const useProductsOverviewStore = create<ProductsOverwiewState>()(
  persist(
    (set, get) => ({
      ...initialState,
      actions: {
        getProductsData: async ({
          lang,
          currentUserTenant,
        }: GetProductStateProps) => {
          if (typeof currentUserTenant !== "string") {
            console.error(
              `TypeError, currentUserTenant of type '${typeof currentUserTenant}' is not a string`,
            );
            return;
          }
          if (typeof lang !== "string") {
            console.error(
              `TypeError, lang of type '${typeof lang}' is not a string`,
            );
            return;
          }
          const newCacheKey = lang + currentUserTenant;
          if (get().cacheKey === newCacheKey) {
            return;
          }
          set({ isLoading: true, error: null });

          Scrivito.load(() => {
            const homepage = getHomepage() as Scrivito.Obj | null | undefined;
            const productListPage = homepage?.get("productListPage") as
              | Scrivito.Obj
              | null
              | undefined;
            const productConfiguratorPage = homepage?.get(
              "productConfiguratorPage",
            ) as Scrivito.Obj | null | undefined;
            set({
              productListPage,
              productConfiguratorPage,
            });
          });

          fetchData({
            query: ProductListQuery,
            variables: {
              input: {
                client: "web",
                languageCode: lang,
                countryCode: currentUserTenant,
              },
            },
            onSuccess: (data) => {
              const result = data.seriesNodes as ProductNode[];
              get().actions.addKeyLists([], result);
              set({
                productListData: result,
                isLoading: false,
                cacheKey: newCacheKey,
              });
              get().actions.generateAllProducts({
                lang,
                productListData: result,
              });
            },
            onError: (error) => set({ error, isLoading: false }),
          });
        },

        generateAllProducts: async ({ lang, productListData }) => {
          await Scrivito.load(() =>
            Scrivito.Obj.where("_objClass", "equals", "WizardItem")
              .and("categoryLanguages", "equals", lang)
              .take()
              .filter((i) => i.get("linkForMenuItem"))
              .map((link) => ({
                name: link.get("title") as string,
                image:
                  (link.get("imageForProductsNavigation") as string) || null,
                key: link.id(),
                link,
              })),
          )
            .then((extraLinks) => {
              const productsListWithWizards = [
                ...productListData,
                ...extraLinks,
              ];
              set({ allProducts: productsListWithWizards });
            })
            .catch((err) =>
              set({ error: err.message || "Error preparing products" }),
            )
            .finally(() => set({ isLoading: false }));
        },

        addKeyLists: (parentKeyList: string[], prods: ProductNode[] = []) =>
          prods.map((item) => {
            const list = [...parentKeyList, item.key];
            item.keyList = list;
            if (item.nodes?.length) {
              get().actions.addKeyLists(list, item.nodes);
            }
            return item;
          }),

        setProductCategoryId: (newVal: string[]) =>
          set((state) => {
            if (!isEqual(state.productCategoryId, newVal)) {
              return { productCategoryId: newVal };
            }
            return state;
          }),

        markSidebarChange: () =>
          set((state) => {
            state.sidebarChangeRef.current = true;
            return state;
          }),
        resetSidebarChange: () =>
          set((state) => {
            state.sidebarChangeRef.current = false;
            return state;
          }),

        setProductsContent: (content: ProductNode[]) => {
          set({ productsContent: content });
        },

        setCategoryHeadline: (headline: string) => {
          set({ categoryHeadline: headline });
        },

        setWaitingFor3dModel: (value: boolean) => {
          set({ waitingFor3dModel: value });
        },

        setIs3dModelError: (error) => {
          set({ is3dModelError: error, waitingFor3dModel: false });
        },

        resetProductConfiguratorStore: () => set(initialState),

        navigateToProductListPage: (params = {}) => {
          const { productListPage } = get();
          if (productListPage) {
            Scrivito.navigateTo(() => productListPage, {
              params: params as Record<string, string>,
            });
          }
        },

        navigateToConfiguratorPage: (params = {}) => {
          const { productConfiguratorPage } = get();
          const seriesId =
            "seriesId" in params ? Number(params.seriesId) : undefined;
          if (!seriesId || isNaN(seriesId)) {
            console.error(
              `Invalid seriesId: ${seriesId}. It must be a valid number.`,
            );
            return;
          }
          if (!productConfiguratorPage) {
            console.error("Product configurator page is not available.");
            return;
          }
          const productActions = getProductActions();
          productActions.loadProduct(
            {
              seriesId: Number(seriesId),
            },
            true,
          );
          Scrivito.navigateTo(() => productConfiguratorPage, {
            params: params as Record<string, string>,
          });
        },
      },
    }),
    {
      name: "product-configurator",
      partialize: (state) => ({
        productCategoryId: state.productCategoryId,
      }),
    },
  ),
);

export const useProductsOverviewActions = () =>
  useProductsOverviewStore((state) => state.actions);

// Todo: implement the below in a refactoring topic to
// - reduce overall code bloat of product list logic
// - improve perforamnce of product list page
// - reduce complexity of product list logic
